import { Trans, useLingui } from "@lingui/react/macro";
import { CrownIcon } from "lucide-react";
import { useState } from "react";

import { useApiErrorMessage } from "@/shared/api";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

import { useBillingStatus } from "../../api/useBillingStatus";
import { useCancelAutopay } from "../../api/useCancelAutopay";
import { formatProDate } from "../../lib/formatProDate";
import { useProPlanLabels } from "../../lib/useProPlanLabels";

type ProStatusCardProps = {
  // Игрок вошёл: у гостя статуса нет.
  signedIn: boolean;
  onExtend: () => void;
};

// «Твой Pro до …» над сравнением тарифов (design/prototype/ProCheckout.dc.html, нижний блок):
// автоплатёж и кнопка «Отключить автоплатёж» с подтверждением — её обещает соглашение (п. 6.4).
// Без действующего Pro карточки нет.
export const ProStatusCard = ({ signedIn, onExtend }: ProStatusCardProps) => {
  const { i18n, t } = useLingui();
  const { price } = useProPlanLabels();
  const status = useBillingStatus(signedIn);
  const cancel = useCancelAutopay();
  const cancelError = useApiErrorMessage(cancel.error);
  const [confirming, setConfirming] = useState(false);
  // Время открытия страницы: Pro, закончившийся раньше, уже не показываем.
  const [openedAt] = useState(() => Date.now());

  const proUntil = status.data?.pro_until ?? null;
  if (proUntil === null || new Date(proUntil).getTime() <= openedAt) {
    return null;
  }
  const until = formatProDate(proUntil, i18n.locale);
  const autopay = status.data?.autopay ?? null;
  const nextChargeAt = autopay?.next_charge_at ?? null;
  const next = nextChargeAt === null ? null : formatProDate(nextChargeAt, i18n.locale);
  const autopayOn = autopay?.status === "active" || autopay?.status === "pending";

  const text = (() => {
    if (autopay?.status === "past_due") {
      return (
        <Trans>
          We couldn't charge the next payment. Pro won't be extended until you pay again.
        </Trans>
      );
    }
    if (autopay?.status === "cancelling") {
      return <Trans>Turning off autopay…</Trans>;
    }
    if (autopayOn) {
      return next === null ? (
        <Trans>Autopay is on.</Trans>
      ) : (
        <Trans>
          Autopay is on: the next {price} will be charged on {next}.
        </Trans>
      );
    }
    return <Trans>Autopay is off. After {until} the free version comes back.</Trans>;
  })();

  return (
    <section
      aria-label={t`Pro`}
      className="flex flex-wrap items-center gap-x-5 gap-y-3.5 rounded-xl border-2 border-accent bg-surface-raised px-5 py-4"
    >
      <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-sidebar-active text-accent">
        <CrownIcon aria-hidden="true" className="size-5.5" />
      </span>
      <span className="flex flex-[1_1_260px] flex-col gap-0.5">
        <span className="font-display text-2xl leading-tight text-accent">
          <Trans>Your Pro until {until}</Trans>
        </span>
        <span className="text-sm text-muted">{text}</span>
      </span>
      {autopayOn ? (
        <Button
          variant="outline"
          className="h-11 border-border-strong font-semibold"
          onClick={() => {
            setConfirming(true);
          }}
        >
          <Trans>Turn off autopay</Trans>
        </Button>
      ) : autopay?.status === "cancelling" ? null : (
        <Button className="h-11 font-semibold" onClick={onExtend}>
          <Trans>Extend</Trans>
        </Button>
      )}

      <Dialog
        open={confirming}
        onOpenChange={(next) => {
          if (!next) {
            cancel.reset();
          }
          setConfirming(next);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans>Turn off autopay?</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>There will be no more charges. Pro stays until {until}.</Trans>
            </DialogDescription>
          </DialogHeader>
          {cancelError !== null && (
            <p role="alert" className="text-sm text-bad">
              {cancelError}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={cancel.isPending}>
                <Trans>Keep</Trans>
              </Button>
            </DialogClose>
            <Button
              className="font-semibold"
              loading={cancel.isPending}
              onClick={() => {
                cancel.mutate(
                  {},
                  {
                    onSuccess: () => {
                      setConfirming(false);
                    },
                  },
                );
              }}
            >
              {cancel.isPending ? <Trans>Turning off…</Trans> : <Trans>Turn off</Trans>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
